/**
 * merge-preview.ts — FE merge resolver (SILL-386 T3 M3, slug fe-preview).
 *
 * Client-side counterpart to backend/internal/services/messaging/merge.go.
 * Implements the identical 8-token grammar with the same fallback strings.
 * Parity is pinned by the shared JSON fixture:
 *   backend/internal/services/crm/testdata/merge_fixture.json
 *
 * Locked decisions (spec D4, D5):
 *   - T1 universal (recipient): {{first_name}}, {{last_name}}, {{full_name}}
 *   - T1 universal (sender):    {{agent_name}}, {{agent_first_name}}
 *   - T2 (client-only): {{band}}, {{qual_price}}, {{target_area}}
 *   - T1 recipient: {{first_name}} — clients use first space-split of displayName;
 *     contacts use firstName field; fallback "there"
 *   - T1 recipient: {{last_name}} — clients use second space-split of displayName;
 *     contacts use lastName field; graceful omit (empty string) when missing
 *   - T1 recipient: {{full_name}} — first+" "+last trimmed; fallback "there" when both empty
 *   - T1 sender: {{agent_name}} — agentName from envelope; fallback "your agent"
 *   - T1 sender: {{agent_first_name}} — first word of agentName; fallback "your agent"
 *   - T2 (client-only): financial tokens always fall back for contacts and
 *     all-pending clients (all T2 fields null)
 *   - Unknown {{...}} sequences pass through literally
 *   - Fallback strings exactly match the Go constants (canonical)
 */

// ─────────────────────────────────────────────────────────────────────────────
// Token + fallback constants (must match merge.go)
// ─────────────────────────────────────────────────────────────────────────────

const TOKEN_FIRST_NAME = "{{first_name}}";
const TOKEN_LAST_NAME = "{{last_name}}";
const TOKEN_FULL_NAME = "{{full_name}}";
const TOKEN_AGENT_NAME = "{{agent_name}}";
const TOKEN_AGENT_FIRST_NAME = "{{agent_first_name}}";
const TOKEN_BAND = "{{band}}";
const TOKEN_QUAL_PRICE = "{{qual_price}}";
const TOKEN_TARGET_AREA = "{{target_area}}";
const TOKEN_TIMELINE = "{{timeline}}";

const FALLBACK_FIRST_NAME = "there";
/** last_name graceful-omit: empty string (surrounding whitespace may produce a space). */
const FALLBACK_LAST_NAME = "";
const FALLBACK_FULL_NAME = "there";
const FALLBACK_AGENT_NAME = "your agent";
const FALLBACK_AGENT_FIRST_NAME = "your agent";
const FALLBACK_BAND = "your price range";
const FALLBACK_QUAL_PRICE = "your budget";
const FALLBACK_TARGET_AREA = "your area";
/** {{timeline}} client-only purchase timeline; nil/empty → "soon". */
const FALLBACK_TIMELINE = "soon";

// ─────────────────────────────────────────────────────────────────────────────
// Brace normalization (allowlist-scoped; mirrors merge.go mergeNormalizeRe +
// mergeTokenAliases). Rewrites KNOWN tokens in single-brace {x} or double-brace
// {{x}} form to their canonical double-brace token BEFORE replacement; the
// {area}→{{target_area}} and {your_name}→{{agent_first_name}} aliases normalize
// too. Unknown {{...}} / {...} prose is NOT in the alternation, so it passes
// through literally. The whole wrapped token is matched as one unit so a
// double-brace form is never corrupted by a single-brace rewrite.
// ─────────────────────────────────────────────────────────────────────────────

const MERGE_TOKEN_ALIASES: Record<string, string> = {
  area: TOKEN_TARGET_AREA,
  your_name: TOKEN_AGENT_FIRST_NAME,
};

const MERGE_NORMALIZE_RE =
  /\{\{(?:first_name|last_name|full_name|agent_name|agent_first_name|band|qual_price|target_area|timeline|area|your_name)\}\}|\{(?:first_name|last_name|full_name|agent_name|agent_first_name|band|qual_price|target_area|timeline|area|your_name)\}/g;

/**
 * normalizeBraces rewrites known single/double-brace tokens (and the area /
 * your_name aliases) to their canonical double-brace form. Parity with
 * merge.go's mergeNormalizeRe.ReplaceAllStringFunc pre-pass.
 */
function normalizeBraces(template: string): string {
  return template.replace(MERGE_NORMALIZE_RE, (m) => {
    const name = m.replace(/[{}]/g, "");
    const alias = MERGE_TOKEN_ALIASES[name];
    return alias ?? `{{${name}}}`;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Recipient types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A connected client recipient. T2 (financial) tokens resolve from the
 * affordability snapshot. Null fields mean "pending / not available" →
 * fallback.
 *
 * Two construction paths are supported:
 *   1. Raw numbers (bandLow/bandHigh/qualPrice) — used in tests via the shared
 *      fixture for byte-equal parity with the Go backend.
 *   2. Pre-formatted string overrides (bandFormatted/qualPriceFormatted) — used
 *      by the UI when the ClientVM only exposes already-formatted strings from
 *      the affordability overlay. When set, these bypass number formatting.
 *
 * For a given field, if the pre-formatted override is non-null/non-undefined,
 * it is used directly. Otherwise the raw numbers are formatted.
 */
export interface ClientRecipient {
  kind: "client";
  /** The consumer_profiles.display_name value. Null → first_name falls back. */
  displayName: string | null;
  /**
   * Recipient's last name. When null/undefined, derived from the second space-
   * split word of displayName. Empty/null → {{last_name}} graceful-omit ("").
   */
  lastName?: string | null;
  /** consumer_profiles.target_area. Null → fallback. */
  targetArea: string | null;
  /**
   * consumer_profiles horizon (purchase timeline). Null/undefined/empty →
   * {{timeline}} fallback ("soon"). Client-only T2 token.
   */
  timeline?: string | null;
  /** Affordability BandLow (dollars). Null → fallback (unless bandFormatted set). */
  bandLow: number | null;
  /** Affordability BandHigh (dollars). Null → fallback (unless bandFormatted set). */
  bandHigh: number | null;
  /** Affordability QualificationPrice (dollars). Null → fallback (unless qualPriceFormatted set). */
  qualPrice: number | null;
  /**
   * Pre-formatted band string (UI path). When non-null, bypasses number
   * formatting and uses this string directly (or falls back if empty).
   * Not used in fixture-parity tests; do not set in raw-number test cases.
   */
  bandFormatted?: string | null;
  /**
   * Pre-formatted qual price string (UI path). When non-null, bypasses
   * number formatting. Not used in fixture-parity tests.
   */
  qualPriceFormatted?: string | null;
}

/**
 * A book-of-business contact recipient. T2 tokens ALWAYS fall back for
 * contacts regardless of any data. This is a type-level invariant:
 * there is no way to pass financial data for a contact.
 */
export interface ContactRecipient {
  kind: "contact";
  /** crm_contacts.first_name. Empty → fallback. */
  firstName: string;
  /** crm_contacts.last_name. Empty → {{last_name}} graceful-omit. */
  lastName?: string;
}

export type MergeRecipient = ClientRecipient | ContactRecipient;

/**
 * Agent (sender) context for the {{agent_name}} / {{agent_first_name}} tokens.
 * When omitted from resolveMerge calls, both tokens fall back.
 */
export interface AgentContext {
  /** The sending professional's full display name. Empty → "your agent". */
  name: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * resolveMerge — replace all eight merge tokens in `template` with
 * recipient-specific resolved values (or canonical fallbacks). Identical
 * output contract to the Go `ResolveMerge` function.
 *
 * Rules:
 *   - T1 {{first_name}}: client → first space-split token of displayName;
 *     contact → firstName. Empty/null → "there".
 *   - T1 {{last_name}}: client → second space-split word of displayName (or
 *     explicit lastName); contact → lastName. Empty → "" (graceful omit).
 *   - T1 {{full_name}}: first+" "+last trimmed; falls back to "there" when both empty.
 *   - T1 {{agent_name}}: agentCtx.name or "your agent".
 *   - T1 {{agent_first_name}}: first word of agentCtx.name or "your agent".
 *   - T2 {{band}}: client → formatted price band; contact/pending → "your price range"
 *   - T2 {{qual_price}}: client → formatted money; contact/pending → "your budget"
 *   - T2 {{target_area}}: client → targetArea; contact/pending → "your area"
 *   - Unknown {{...}} tokens pass through literally.
 *
 * @param agentCtx - optional sender context for {{agent_name}} / {{agent_first_name}}.
 */
export function resolveMerge(
  template: string,
  recipient: MergeRecipient,
  agentCtx?: AgentContext,
): string {
  // Brace normalization (allowlist-scoped) — mirror merge.go's pre-pass.
  let result = normalizeBraces(template);

  // T1: first_name — universal
  result = replaceAll(result, TOKEN_FIRST_NAME, resolveFirstName(recipient));

  // T1: last_name — universal, graceful-omit
  result = replaceAll(result, TOKEN_LAST_NAME, resolveLastName(recipient));

  // T1: full_name — universal
  result = replaceAll(result, TOKEN_FULL_NAME, resolveFullName(recipient));

  // T1: agent_name + agent_first_name — universal sender tokens
  result = replaceAll(result, TOKEN_AGENT_NAME, resolveAgentName(agentCtx));
  result = replaceAll(result, TOKEN_AGENT_FIRST_NAME, resolveAgentFirstName(agentCtx));

  // T2: financial — client-only
  result = replaceAll(result, TOKEN_TARGET_AREA, resolveTargetArea(recipient));
  result = replaceAll(result, TOKEN_BAND, resolveBand(recipient));
  result = replaceAll(result, TOKEN_QUAL_PRICE, resolveQualPrice(recipient));

  // T2: timeline — client-only
  result = replaceAll(result, TOKEN_TIMELINE, resolveTimeline(recipient));

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Segment-emitting variant for styled preview rendering
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A text segment emitted by resolveMergeSegments.
 *   kind "figure" — a resolved financial data value ($650K–$720K, $685K).
 *                   Only actual resolved data values are figures; fallback
 *                   strings ("your price range", "your budget") are prose.
 *   kind "text"   — all other text: prose, first_name, target_area, fallbacks,
 *                   surrounding copy.
 */
export interface MergeSegment {
  text: string;
  kind: "text" | "figure";
}

/**
 * resolveMergeSegments — segment-aware variant of resolveMerge.
 *
 * Returns an array of MergeSegment where each token position is emitted as a
 * separate segment. This lets the caller render "figure" segments with
 * font-mono while keeping surrounding text as sans-serif.
 *
 * Classification rules:
 *   - {{band}} resolved to a REAL data value (not a fallback) → "figure"
 *   - {{qual_price}} resolved to a REAL data value (not a fallback) → "figure"
 *   - {{first_name}}, {{last_name}}, {{full_name}} → "text" (name reads as prose)
 *   - {{agent_name}}, {{agent_first_name}} → "text" (prose)
 *   - {{target_area}} resolved value → "text" (prose)
 *   - Fallback strings ("your price range", "your budget") → "text"
 *   - Surrounding template text → "text"
 *
 * Invariant: segments.map(s => s.text).join("") === resolveMerge(template, recipient, agentCtx)
 * Empty segments (empty text) are dropped from the output.
 */
export function resolveMergeSegments(
  template: string,
  recipient: MergeRecipient,
  agentCtx?: AgentContext,
): MergeSegment[] {
  // Determine resolved values for each token and their kinds.
  const firstName = resolveFirstName(recipient);
  const lastName = resolveLastName(recipient);
  const fullName = resolveFullName(recipient);
  const agentName = resolveAgentName(agentCtx);
  const agentFirstName = resolveAgentFirstName(agentCtx);
  const targetArea = resolveTargetArea(recipient);
  const band = resolveBand(recipient);
  const qualPrice = resolveQualPrice(recipient);
  const timeline = resolveTimeline(recipient);

  // A band value is a "figure" only when it is a real resolved value, not a fallback.
  const bandIsFigure = band !== FALLBACK_BAND;
  // A qual_price value is a "figure" only when it is a real resolved value.
  const qualPriceIsFigure = qualPrice !== FALLBACK_QUAL_PRICE;

  // Token→replacement pairs with associated kinds.
  // Order mirrors resolveMerge.
  type TokenDef = { token: string; value: string; kind: "text" | "figure" };
  const tokens: TokenDef[] = [
    { token: TOKEN_FIRST_NAME, value: firstName, kind: "text" },
    { token: TOKEN_LAST_NAME, value: lastName, kind: "text" },
    { token: TOKEN_FULL_NAME, value: fullName, kind: "text" },
    { token: TOKEN_AGENT_NAME, value: agentName, kind: "text" },
    { token: TOKEN_AGENT_FIRST_NAME, value: agentFirstName, kind: "text" },
    { token: TOKEN_TARGET_AREA, value: targetArea, kind: "text" },
    { token: TOKEN_BAND, value: band, kind: bandIsFigure ? "figure" : "text" },
    { token: TOKEN_QUAL_PRICE, value: qualPrice, kind: qualPriceIsFigure ? "figure" : "text" },
    { token: TOKEN_TIMELINE, value: timeline, kind: "text" },
  ];

  // Split template into segments by applying tokens sequentially.
  // Each pass takes the current list of {text, kind:"text"} segments and splits
  // on the token, injecting a resolved segment at each split point.
  let parts: MergeSegment[] = [{ text: normalizeBraces(template), kind: "text" }];

  for (const { token, value, kind } of tokens) {
    const next: MergeSegment[] = [];
    for (const seg of parts) {
      if (seg.kind !== "text") {
        // Already-resolved segments (figure) are not re-split.
        next.push(seg);
        continue;
      }
      const pieces = seg.text.split(token);
      for (let i = 0; i < pieces.length; i++) {
        if (pieces[i] !== "") {
          next.push({ text: pieces[i], kind: "text" });
        }
        if (i < pieces.length - 1) {
          // Inject the resolved token value.
          if (value !== "") {
            next.push({ text: value, kind });
          }
        }
      }
    }
    parts = next;
  }

  // Drop empty segments (can arise from adjacent tokens or empty template).
  return parts.filter((s) => s.text !== "");
}

// ─────────────────────────────────────────────────────────────────────────────
// Unresolved-token segment splitter (no recipient) — for surfaces that show a
// per-audience preview body BEFORE per-recipient resolution (e.g. the
// draft_campaign confirm card, plan 188 task 817). Splits a raw template into
// text/token segments without resolving values, so a caller can render each
// merge token as a highlighted chip instead of literal `{{first_name}}` text.
// ─────────────────────────────────────────────────────────────────────────────

/** A segment emitted by splitMergeTokens: either literal text or a raw token. */
export interface MergeTokenSegment {
  /** The literal text, or the raw canonical token (e.g. "{{first_name}}"). */
  text: string;
  isToken: boolean;
}

const KNOWN_MERGE_TOKENS = [
  TOKEN_FIRST_NAME,
  TOKEN_LAST_NAME,
  TOKEN_FULL_NAME,
  TOKEN_AGENT_NAME,
  TOKEN_AGENT_FIRST_NAME,
  TOKEN_BAND,
  TOKEN_QUAL_PRICE,
  TOKEN_TARGET_AREA,
  TOKEN_TIMELINE,
];

/** Friendly chip labels for each canonical token (parity with Composer's TOKENS chip strings). */
export const MERGE_TOKEN_CHIP_LABELS: Record<string, string> = {
  [TOKEN_FIRST_NAME]: "First name",
  [TOKEN_LAST_NAME]: "Last name",
  [TOKEN_FULL_NAME]: "Full name",
  [TOKEN_AGENT_NAME]: "Your name",
  [TOKEN_AGENT_FIRST_NAME]: "Your first name",
  [TOKEN_BAND]: "Price band",
  [TOKEN_QUAL_PRICE]: "Qual price",
  [TOKEN_TARGET_AREA]: "Target area",
  [TOKEN_TIMELINE]: "Timeline",
};

const MERGE_TOKEN_SPLIT_RE = new RegExp(
  `(${KNOWN_MERGE_TOKENS.map((t) => t.replace(/[{}]/g, "\\$&")).join("|")})`,
  "g",
);

/**
 * splitMergeTokens — split `template` into text/token segments WITHOUT
 * resolving recipient values. Known single-brace/alias forms are normalized
 * to their canonical double-brace token first (mirrors resolveMerge's
 * pre-pass), so `{area}` and `{{target_area}}` both split as a token segment.
 * Unknown `{{...}}` sequences pass through as plain text.
 */
export function splitMergeTokens(template: string): MergeTokenSegment[] {
  const normalized = normalizeBraces(template);
  return normalized
    .split(MERGE_TOKEN_SPLIT_RE)
    .filter((part) => part !== "")
    .map((part) => ({
      text: part,
      isToken: (KNOWN_MERGE_TOKENS as string[]).includes(part),
    }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Token resolvers
// ─────────────────────────────────────────────────────────────────────────────

function resolveFirstName(r: MergeRecipient): string {
  let raw: string;
  if (r.kind === "client") {
    raw = r.displayName != null ? r.displayName.trim() : "";
    // Take the first space-split token ("Alice Smith" → "Alice")
    const spaceIdx = raw.indexOf(" ");
    if (spaceIdx > 0) {
      raw = raw.slice(0, spaceIdx);
    }
  } else {
    raw = r.firstName.trim();
  }
  return raw === "" ? FALLBACK_FIRST_NAME : raw;
}

/**
 * resolveLastName — T1 universal, graceful-omit.
 * For clients: uses explicit lastName if provided, otherwise the second
 * space-split word of displayName.
 * For contacts: uses the lastName field.
 * Empty → "" (FALLBACK_LAST_NAME = "").
 */
function resolveLastName(r: MergeRecipient): string {
  if (r.kind === "client") {
    // Explicit override takes precedence.
    if (r.lastName != null && r.lastName !== undefined) {
      return r.lastName.trim();
    }
    // Derive from displayName second word.
    const dn = r.displayName != null ? r.displayName.trim() : "";
    const spaceIdx = dn.indexOf(" ");
    if (spaceIdx > 0) {
      return dn.slice(spaceIdx + 1).trim();
    }
    return FALLBACK_LAST_NAME;
  } else {
    return (r.lastName ?? "").trim();
  }
}

/**
 * resolveFullName — T1 universal.
 * first + " " + last, trimmed. Falls back to "there" when both are empty.
 */
function resolveFullName(r: MergeRecipient): string {
  const first = resolveFirstNameRaw(r);
  const last = resolveLastName(r);
  if (first === "" && last === "") {
    return FALLBACK_FULL_NAME;
  }
  if (last === "") {
    return first;
  }
  if (first === "") {
    return last;
  }
  return `${first} ${last}`;
}

/**
 * resolveFirstNameRaw — raw first name before fallback.
 * Returns "" when no first name is available (used internally by resolveFullName).
 */
function resolveFirstNameRaw(r: MergeRecipient): string {
  let raw: string;
  if (r.kind === "client") {
    raw = r.displayName != null ? r.displayName.trim() : "";
    const spaceIdx = raw.indexOf(" ");
    if (spaceIdx > 0) {
      raw = raw.slice(0, spaceIdx);
    }
  } else {
    raw = r.firstName.trim();
  }
  return raw;
}

/**
 * resolveAgentName — T1 universal sender.
 * Returns agentCtx.name when non-empty; falls back to FALLBACK_AGENT_NAME.
 */
function resolveAgentName(agentCtx?: AgentContext): string {
  if (agentCtx && agentCtx.name.trim() !== "") {
    return agentCtx.name.trim();
  }
  return FALLBACK_AGENT_NAME;
}

/**
 * resolveAgentFirstName — T1 universal sender.
 * Returns first space-split word of agentCtx.name; falls back to FALLBACK_AGENT_FIRST_NAME.
 */
function resolveAgentFirstName(agentCtx?: AgentContext): string {
  if (agentCtx && agentCtx.name.trim() !== "") {
    const name = agentCtx.name.trim();
    const spaceIdx = name.indexOf(" ");
    return spaceIdx > 0 ? name.slice(0, spaceIdx) : name;
  }
  return FALLBACK_AGENT_FIRST_NAME;
}

function resolveTargetArea(r: MergeRecipient): string {
  if (r.kind !== "client" || r.targetArea == null) {
    return FALLBACK_TARGET_AREA;
  }
  const v = r.targetArea.trim();
  return v === "" ? FALLBACK_TARGET_AREA : v;
}

/**
 * resolveTimeline — T2 client-only purchase timeline ({{timeline}}).
 * Contacts (non-client), null/undefined timeline, or an empty/whitespace-only
 * timeline all fall back to "soon". Mirrors merge.go resolveTimeline.
 */
function resolveTimeline(r: MergeRecipient): string {
  if (r.kind !== "client" || r.timeline == null) {
    return FALLBACK_TIMELINE;
  }
  const v = r.timeline.trim();
  return v === "" ? FALLBACK_TIMELINE : v;
}

function resolveBand(r: MergeRecipient): string {
  if (r.kind !== "client") {
    return FALLBACK_BAND;
  }
  // Pre-formatted override path (UI: ClientVM has already-formatted strings)
  if (r.bandFormatted != null) {
    const v = r.bandFormatted.trim();
    return v === "" ? FALLBACK_BAND : v;
  }
  return formatBand(r.bandLow, r.bandHigh);
}

function resolveQualPrice(r: MergeRecipient): string {
  if (r.kind !== "client") {
    return FALLBACK_QUAL_PRICE;
  }
  // Pre-formatted override path (UI: ClientVM has already-formatted strings)
  if (r.qualPriceFormatted != null) {
    const v = r.qualPriceFormatted.trim();
    return v === "" ? FALLBACK_QUAL_PRICE : v;
  }
  if (r.qualPrice == null) {
    return FALLBACK_QUAL_PRICE;
  }
  const v = formatMoney(r.qualPrice);
  return v === "" ? FALLBACK_QUAL_PRICE : v;
}

// ─────────────────────────────────────────────────────────────────────────────
// Money formatting (must produce identical output to merge.go formatBand /
// formatMoney — both suites assert against the shared fixture)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * formatBand builds the human-readable price band string. Rules match merge.go:
 *   both present:  "$400K–$550K"
 *   high only:     "up to $600K"
 *   low only:      "from $400K"
 *   neither:       "your price range"
 */
function formatBand(low: number | null, high: number | null): string {
  if (low != null && high != null) {
    return `${formatMoney(low)}–${formatMoney(high)}`;
  }
  if (high != null) {
    return `up to ${formatMoney(high)}`;
  }
  if (low != null) {
    return `from ${formatMoney(low)}`;
  }
  return FALLBACK_BAND;
}

/**
 * formatMoney formats a dollar amount (in dollars, not cents) to a compact
 * string. Mirrors merge.go formatMoney exactly:
 *   0:           "$0"
 *   < 1000:      "$NNN"       (e.g. "$999")
 *   1000–999999: "$NNNK"      (capital K, rounded to nearest thousand)
 *   >= 1000000:  "$N.NM" etc  (one or two decimal places, no trailing zeros)
 */
function formatMoney(n: number): string {
  if (n < 1000) {
    return `$${n}`;
  }
  if (n < 1_000_000) {
    const k = Math.floor(n / 1000);
    return `$${k}K`;
  }
  // Millions — match Go's exact logic
  const whole = Math.floor(n / 1_000_000);
  const frac = Math.floor((n % 1_000_000) / 100_000); // tenths
  if (frac === 0) {
    return `$${whole}.0M`;
  }
  const frac2 = Math.floor((n % 1_000_000) / 10_000); // hundredths
  if (frac2 % 10 === 0) {
    // one significant decimal
    return `$${whole}.${frac}M`;
  }
  return `$${whole}.${frac2.toString().padStart(2, "0")}M`;
}

// ─────────────────────────────────────────────────────────────────────────────
// VM helpers — derive merge recipients from view models (UI path)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derive a ClientRecipient from a ClientVM for use in the preview pane.
 *
 * The VM carries already-formatted affordability strings; raw numbers are not
 * stored in the view model. We use the pre-formatted override fields so the
 * merge resolver can use them directly.
 *
 * `ClientVM` is typed loosely here (duck-typed intersection) to avoid a circular
 * import between lib/ modules. The fields we need are:
 *   - name: string
 *   - area: string
 *   - affordability?: { band: string | null; qualPrice: string | null }
 */
export function clientVMToRecipient(vm: {
  name: string;
  area?: string;
  affordability?: {
    band: string | null;
    qualPrice: string | null;
  } | null;
}): ClientRecipient {
  const targetArea =
    vm.area && vm.area !== "Not shared" ? vm.area : null;
  // Derive lastName from the second space-split word of the display name.
  // This mirrors the backend secondWordMessaging helper in messaging_adapters.go.
  // ClientVM.name is already formatted ("Jordan Kim") so we can extract it here.
  const nameParts = (vm.name ?? "").trim().split(/\s+/);
  const lastName = nameParts.length >= 2 ? nameParts[nameParts.length - 1] : undefined;
  return {
    kind: "client",
    displayName: vm.name ?? null,
    lastName,
    targetArea,
    // Raw numbers not available in the VM — use null so formatBand falls back
    // unless the pre-formatted override is set.
    bandLow: null,
    bandHigh: null,
    qualPrice: null,
    bandFormatted: vm.affordability?.band ?? null,
    qualPriceFormatted: vm.affordability?.qualPrice ?? null,
  };
}

/**
 * Derive a ContactRecipient from a ContactVM for use in the preview pane.
 * Uses the contact's explicit first_name / last_name when present; falls back
 * to splitting the display name for forward-compatibility.
 *
 * `ContactVM.name` is already resolved (falls back to email local-part etc.),
 * but we use the explicit fields to match the backend contact.first_name /
 * contact.last_name semantics so {{last_name}} and {{full_name}} render
 * correctly in the preview.
 */
export function contactVMToRecipient(vm: {
  name: string;
  lastName?: string;
}): ContactRecipient {
  // Prefer the explicit first token of the name as firstName
  const firstName = vm.name?.split(" ")[0]?.trim() ?? "";
  return {
    kind: "contact",
    firstName,
    lastName: vm.lastName,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Replace all occurrences of `token` with `value` in `str`. */
function replaceAll(str: string, token: string, value: string): string {
  // String.prototype.replaceAll is available in all modern browsers and Node 15+;
  // vitest/node 18+ is fine. Avoids a regex escape dependency.
  return str.split(token).join(value);
}
